import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, Edit2, Trash2, ExternalLink, Shield } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useUserCertifications, UserCertification } from '@/hooks/useUserCertifications';
import { useTranslatedToast } from '@/hooks/useTranslatedToast';
import { CertificationForm } from './CertificationForm';

interface CertificationsSectionProps {
  userId?: string;
  isEditing?: boolean;
}

export const CertificationsSection = ({ userId, isEditing = false }: CertificationsSectionProps) => {
  const { t, i18n } = useTranslation('ui');
  const { certifications, loading, addCertification, updateCertification, deleteCertification } = useUserCertifications(userId);
  const toast = useTranslatedToast();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    issuer: '',
    date: '',
    description: '',
    url: ''
  });

  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      issuer: '',
      date: '',
      description: '',
      url: ''
    });
  }, []);

  const loadFormData = useCallback((certification: UserCertification) => {
    setFormData({
      name: certification.name || '',
      issuer: certification.issuer || '',
      date: certification.date || '',
      description: certification.description || '',
      url: certification.url || ''
    });
  }, []);

  const handleSave = useCallback(async () => {
    try {
      const dataToSave = {
        ...formData,
        issuer: formData.issuer || null,
        date: formData.date || null,
        description: formData.description || null,
        url: formData.url || null
      };

      if (editingId) {
        await updateCertification(editingId, dataToSave);
        toast.success('profile_sections.certification_updated');
        setEditingId(null);
      } else {
        await addCertification(dataToSave);
        toast.success('profile_sections.certification_added');
        setShowAddForm(false);
      }
      resetForm();
    } catch (err) {
      toast.error('profile_sections.certification_save_failed');
    }
  }, [formData, editingId, updateCertification, addCertification, toast, resetForm]);

  const handleDelete = useCallback(async (id: string) => {
    try {
      await deleteCertification(id);
      toast.success('success.deleted');
    } catch (err) {
      toast.error('error.generic');
    }
  }, [deleteCertification, toast]);

  const handleFormDataChange = useCallback((field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleCancel = useCallback(() => {
    if (editingId) {
      setEditingId(null);
    } else {
      setShowAddForm(false);
    }
    resetForm();
  }, [editingId, resetForm]);

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    
    // 根据当前语言设置本地化格式
    const languageMap: { [key: string]: string } = {
      'en': 'en-US',
      'es': 'es-ES', 
      'zh': 'zh-CN',
      'fr': 'fr-FR',
      'ja': 'ja-JP'
    };
    
    const locale = languageMap[i18n.language] || 'en-US';
    
    return new Date(dateString).toLocaleDateString(locale, { 
      year: 'numeric', 
      month: 'short' 
    });
  };

  if (loading) {
    return <div className="animate-pulse space-y-4">{t('profile_sections.loading_certifications')}</div>;
  }


  return (
    <div className="space-y-4">
      {isEditing && (
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAddForm(true)}
            disabled={showAddForm || editingId !== null}
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('profile_sections.add_certification')}
          </Button>
        </div>
      )}

      {(showAddForm || editingId) && (
        <CertificationForm
          formData={formData}
          onFormDataChange={handleFormDataChange}
          onSave={handleSave}
          onCancel={handleCancel}
          editingId={editingId || undefined}
        />
      )}

      {certifications.length === 0 && !showAddForm ? (
        <div className="py-12 text-center">
          <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">{t('profile_sections.no_certifications_title')}</h3>
          <p className="text-muted-foreground text-sm">
            {t('profile_sections.no_certifications_description')}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {certifications.map((certification) => {
            if (editingId === certification.id) return null;

            return (
              <div key={certification.id} className="border rounded-lg p-4">
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      {/* Certification name as primary heading with link */}
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-bold text-foreground">
                          {certification.name}
                        </h3>
                        {certification.url && (
                          <Button variant="ghost" size="sm" asChild>
                            <a href={certification.url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </Button>
                        )}
                      </div>
                      
                      {/* Issuer as secondary text */}
                      {certification.issuer && (
                        <p className="text-base text-muted-foreground mb-3">
                          {certification.issuer}
                        </p>
                      )}
                      
                      {/* Tags for date */}
                      <div className="flex items-center flex-wrap gap-2 text-xs mb-4">
                        {certification.date && (
                          <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted text-muted-foreground">
                            <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full"></div>
                            {t('profile_sections.issued')}: {formatDate(certification.date)}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {isEditing && (
                      <div className="flex gap-2 ml-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingId(certification.id);
                            loadFormData(certification);
                          }}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(certification.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Description with proper typography */}
                  {certification.description && (
                    <div className="space-y-2">
                      <p className="text-sm text-foreground leading-relaxed">
                        {certification.description}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};